<!-- 연차 통계 · 사용촉진 (데스크 이상)
     계산은 전부 서버(/api/leave/stats · /promotion/targets)에서 한다 —
     근로기준법 제60·61조 기준이며, 데일리안처럼 화면에서 하루씩 순회하지 않는다. -->
<template>
  <div id="leave-stats">
    <vx-card>
      <vs-tabs>
        <!-- ===== 전체 인원 연차 현황 ===== -->
        <vs-tab label="연차 현황">
          <div class="flex flex-wrap items-center mb-4">
            <span class="mr-2">기준일</span>
            <datepicker :format="'yyyy-MM-dd'" v-model="asOf" @input="loadStats" class="ls-date mr-4"/>
            <vs-checkbox v-model="onlyActive" @change="loadStats" class="mr-4">재직 직원만</vs-checkbox>
            <vs-input v-model="keyword" placeholder="이름·부서 검색" class="mr-4"/>
            <span class="ls-count">{{ filteredRows.length }}명</span>
          </div>

          <div class="ls-scroll">
            <table class="ls-table">
              <thead>
                <tr>
                  <th>성명</th><th>부서</th><th>입사일</th><th>근속</th>
                  <th>법정발생</th><th>추가</th><th>공제</th><th>총연차</th>
                  <th>결재완료</th><th>신청중</th><th>잔여</th>
                  <th>연차외</th><th>출근율</th><th>소멸일</th><th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in filteredRows" :key="r.member_id"
                    :class="{ 'ls-expire-soon': dday(r.expire_on) <= 60 && r.remain_days > 0 }">
                  <td class="ls-name">{{ r.member_name }}</td>
                  <td>{{ r.department }}</td>
                  <td>{{ r.joined_at }}</td>
                  <td>{{ r.grant_year }}년</td>
                  <td>{{ r.base_days }}</td>
                  <td :class="r.added_days > 0 ? 'ls-added' : ''">{{ r.added_days || '' }}</td>
                  <td :class="r.deducted_days > 0 ? 'ls-deducted' : ''">{{ r.deducted_days || '' }}</td>
                  <td class="ls-strong">{{ r.total_days }}</td>
                  <td>{{ r.used_days }}</td>
                  <td :class="r.requesting_days > 0 ? 'ls-pending' : ''">{{ r.requesting_days || '' }}</td>
                  <td class="ls-strong">{{ r.remain_days }}</td>
                  <td>{{ r.other_used_days || '' }}</td>
                  <td>
                    <span v-if="r.attendance_rate !== null" class="ls-rate">{{ r.attendance_rate }}%</span>
                    <span v-else class="ls-muted">—</span>
                  </td>
                  <td>
                    {{ r.expire_on }}
                    <span v-if="r.no_expire" class="ls-muted">(소멸없음)</span>
                    <span v-else-if="r.remain_days > 0" :class="dday(r.expire_on) <= 60 ? 'ls-warn' : 'ls-muted'">
                      D-{{ dday(r.expire_on) }}
                    </span>
                  </td>
                  <td><vs-button size="small" type="border" @click="openGrant(r)">조정</vs-button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </vs-tab>

        <!-- ===== 사용촉진 (제61조) ===== -->
        <vs-tab label="사용촉진">
          <vs-alert color="primary" active="true" class="mb-4">
            <b>근로기준법 제61조</b> — 아래 절차를 모두 지켜야 미사용 연차에 대한 수당 지급 의무가 면제됩니다.
            소멸 <b>6개월 전을 기준으로 10일 이내</b>에 미사용 일수를 알리고 사용 시기 통보를 서면 촉구 →
            근로자가 <b>10일 이내</b> 미통보 시 → 소멸 <b>2개월 전까지</b> 사용자가 시기를 정해 서면 통보.
            이 시기를 벗어나면 노사 합의가 있어도 법상 사용촉진으로 보지 않습니다.
          </vs-alert>

          <div class="flex items-center mb-4">
            <span class="ls-count mr-4">대상 {{ targets.length }}명</span>
            <span v-if="overdueCount > 0" class="ls-warn">기한 초과 {{ overdueCount }}명</span>
          </div>

          <div class="ls-scroll">
            <table class="ls-table">
              <thead>
                <tr>
                  <th>성명</th><th>부서</th><th>잔여</th><th>소멸일</th>
                  <th>1차 촉구기한</th><th>지정통보 기한</th><th>진행</th><th>다음 조치</th><th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in targets" :key="r.member_id"
                    :class="{ 'ls-overdue': r.notify_overdue || r.designate_overdue }">
                  <td class="ls-name">{{ r.member_name }}</td>
                  <td>{{ r.department }}</td>
                  <td class="ls-strong">{{ r.remain_days }}일</td>
                  <td>{{ r.expire_on }}</td>
                  <td>{{ r.deadlines.notify_from }} ~ {{ r.deadlines.notify_to }}</td>
                  <td>{{ r.deadlines.designate_by }}</td>
                  <td>
                    <span v-if="!r.promotions.length" class="ls-muted">없음</span>
                    <span v-for="(p, i) in r.promotions" :key="i" class="ls-stage">
                      {{ stageText(p.stage) }} {{ p.acted_on }}
                    </span>
                  </td>
                  <td>
                    <span v-if="r.notify_overdue" class="ls-warn">1차 촉구 기한 초과</span>
                    <span v-else-if="r.designate_overdue" class="ls-warn">지정통보 기한 초과</span>
                    <span v-else-if="r.next_stage">{{ stageText(r.next_stage) }}</span>
                    <span v-else class="ls-muted">완료</span>
                  </td>
                  <td><vs-button size="small" type="border" @click="openRecord(r)">기록</vs-button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </vs-tab>
      </vs-tabs>
    </vx-card>

    <!-- 부여 조정 (연차별) -->
    <vs-popup title="연차 부여 조정" :active.sync="grantModal">
      <div v-if="grantTarget">
        <p class="mb-3">
          <b>{{ grantTarget.member_name }}</b> · 입사 {{ grantTarget.joined_at }} · 현재 {{ grantTarget.grant_year }}년차
        </p>

        <!-- 연차별 이력. 조정할 연차를 고른다.
             leave_grant 가 (member_id, grant_year) 로 저장하므로 연차마다 따로 남는다. -->
        <div class="ls-scroll mb-4">
          <table class="ls-table ls-table-sm">
            <thead>
              <tr><th></th><th>연차</th><th>사용 기간</th><th>법정</th><th>추가</th><th>공제</th><th>총</th><th>사용</th><th>잔여</th></tr>
            </thead>
            <tbody>
              <tr v-for="g in grantYears" :key="g.grant_year"
                  :class="{ 'ls-row-sel': g.grant_year === selectedYear }"
                  @click="selectYear(g)">
                <td><input type="radio" :value="g.grant_year" v-model="selectedYear" @change="selectYear(g)"></td>
                <td class="ls-strong">{{ g.grant_year }}년차</td>
                <td>{{ g.accrue_from }} ~ {{ g.expire_on }}</td>
                <td>{{ g.base_days }}</td>
                <td :class="g.added_days > 0 ? 'ls-added' : ''">{{ g.added_days || '' }}</td>
                <td :class="g.deducted_days > 0 ? 'ls-deducted' : ''">{{ g.deducted_days || '' }}</td>
                <td class="ls-strong">{{ g.total_days }}</td>
                <td>{{ g.used_days }}</td>
                <td>{{ g.remain_days }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="mb-3 ls-sel-note">
          <b>{{ selectedYear }}년차</b> 를 조정합니다
          <span v-if="selectedGrant"> ({{ selectedGrant.accrue_from }} ~ {{ selectedGrant.expire_on }})</span>
        </p>
        <div class="mb-3">
          <span class="ls-label">법정 발생일수</span>
          <vs-input type="number" step="0.5" v-model="grantForm.base_days"/>
          <span class="ls-hint">비우고 저장하면 출근율을 반영해 법정값으로 다시 계산합니다(제60조 ①②④, 상한 25일).</span>
        </div>
        <div class="mb-3">
          <span class="ls-label">추가</span>
          <vs-input type="number" step="0.5" v-model="grantForm.added_days"/>
          <span class="ls-hint">사내 약정휴가·이월 등 법정 초과분</span>
        </div>
        <div class="mb-3">
          <span class="ls-label">공제</span>
          <vs-input type="number" step="0.5" v-model="grantForm.deducted_days"/>
        </div>
        <div class="mb-3">
          <span class="ls-label">출근율 (%)</span>
          <vs-input type="number" step="0.01" v-model="grantForm.attendance_rate" placeholder="비우면 80% 이상으로 간주"/>
          <span class="ls-hint">80% 미만이면 제60조② 에 따라 1개월 개근당 1일(최대 11일)로 산정됩니다.</span>
        </div>
        <div class="mb-3">
          <vs-checkbox v-model="grantForm.no_expire">사용자 귀책으로 소멸하지 않음 (제60조⑦ 단서)</vs-checkbox>
        </div>
        <div class="mb-3">
          <span class="ls-label">비고</span>
          <vs-input v-model="grantForm.note"/>
        </div>
        <vs-button :disabled="saving" @click="saveGrant">저장</vs-button>
      </div>
    </vs-popup>

    <!-- 촉진 기록 -->
    <vs-popup title="사용촉진 조치 기록" :active.sync="recordModal">
      <div v-if="recordTarget">
        <p class="mb-3">
          <b>{{ recordTarget.member_name }}</b> · 잔여 {{ recordTarget.remain_days }}일 · 소멸 {{ recordTarget.expire_on }}
        </p>
        <div class="mb-3">
          <span class="ls-label">조치 단계</span>
          <v-select label="text" :options="stageOptions" :reduce="o => o.value"
                    v-model="recordForm.stage" :clearable="false"/>
        </div>
        <div class="mb-3">
          <span class="ls-label">조치일</span>
          <datepicker :format="'yyyy-MM-dd'" v-model="recordForm.acted_on" class="ls-date"/>
          <span class="ls-hint">법정 기한 준수 판단의 근거가 되는 날짜입니다.</span>
        </div>
        <div class="mb-3">
          <span class="ls-label">서면 근거</span>
          <vs-input v-model="recordForm.document" placeholder="파일명·메일 식별자 등"/>
          <span class="ls-hint">분쟁 시 서면 촉구 사실은 사용자가 증명해야 합니다. 근거를 남겨 두세요.</span>
        </div>
        <div class="mb-3">
          <span class="ls-label">비고</span>
          <vs-input v-model="recordForm.note"/>
        </div>
        <vs-button :disabled="saving" @click="saveRecord">기록</vs-button>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker';
import vSelect from 'vue-select';

function ymd(d) {
  if (!d) return '';
  const dt = new Date(d);
  const p = n => (n < 10 ? '0' : '') + n;
  return dt.getFullYear() + '-' + p(dt.getMonth() + 1) + '-' + p(dt.getDate());
}

export default {
  components: { Datepicker, 'v-select': vSelect },
  data() {
    return {
      asOf: new Date(),
      onlyActive: true,
      keyword: '',
      rows: [],
      targets: [],
      saving: false,
      grantModal: false,
      grantTarget: null,
      grantYears: [],      // /grant/list 결과 (연차별 이력)
      selectedYear: 0,
      grantForm: { base_days: '', added_days: 0, deducted_days: 0, attendance_rate: '', no_expire: false, note: '' },
      recordModal: false,
      recordTarget: null,
      recordForm: { stage: 'notify', acted_on: new Date(), document: '', note: '' },
      stageOptions: [
        { text: '1차 서면촉구 (소멸 6개월 전 기준 10일 이내)', value: 'notify' },
        { text: '근로자 사용시기 통보 (촉구 후 10일 이내)', value: 'employee_reply' },
        { text: '사용자 지정통보 (소멸 2개월 전까지)', value: 'designate' },
      ],
    };
  },
  computed: {
    filteredRows() {
      const k = (this.keyword || '').trim();
      if (!k) return this.rows;
      return this.rows.filter(r =>
        (r.member_name || '').indexOf(k) >= 0 || (r.department || '').indexOf(k) >= 0);
    },
    overdueCount() {
      return this.targets.filter(r => r.notify_overdue || r.designate_overdue).length;
    },
    selectedGrant() {
      return this.grantYears.find(g => g.grant_year === this.selectedYear) || null;
    },
  },
  methods: {
    dday(expireOn) {
      if (!expireOn) return 0;
      const exp = new Date(expireOn + 'T00:00:00');
      const base = new Date(ymd(this.asOf) + 'T00:00:00');
      return Math.max(0, Math.round((exp - base) / 86400000));
    },
    stageText(stage) {
      if (stage === 'notify') return '1차 서면촉구';
      if (stage === 'employee_reply') return '사용시기 통보';
      if (stage === 'designate') return '지정통보';
      return stage;
    },
    loadStats() {
      this.$http.post('/api/leave/stats', { as_of: ymd(this.asOf), only_active: this.onlyActive })
        .then(res => { this.rows = res.data.rows || []; })
        .catch(e => this.httpError(e));
    },
    loadTargets() {
      this.$http.post('/api/leave/promotion/targets', { as_of: ymd(this.asOf) })
        .then(res => { this.targets = res.data.rows || []; })
        .catch(e => this.httpError(e));
    },
    openGrant(r) {
      this.grantTarget = r;
      this.grantYears = [];
      this.selectedYear = r.grant_year;
      this.fillForm(r);
      this.grantModal = true;
      // 연차별 이력을 불러와 지난 연차도 고칠 수 있게 한다.
      this.$http.post('/api/leave/grant/list', { member_id: r.member_id, as_of: ymd(this.asOf) })
        .then(res => {
          this.grantYears = res.data.rows || [];
          const cur = this.grantYears.find(g => g.grant_year === this.selectedYear);
          if (cur) this.fillForm(cur);
        })
        .catch(e => this.httpError(e));
    },
    fillForm(g) {
      this.grantForm = {
        base_days: g.base_days,
        added_days: g.added_days,
        deducted_days: g.deducted_days,
        attendance_rate: g.attendance_rate === null ? '' : g.attendance_rate,
        no_expire: !!g.no_expire,
        note: g.note || '',
      };
    },
    selectYear(g) {
      this.selectedYear = g.grant_year;
      this.fillForm(g);
    },
    saveGrant() {
      this.saving = true;
      const body = {
        member_id: this.grantTarget.member_id,
        as_of: ymd(this.asOf),
        grant_year: this.selectedYear,
        added_days: Number(this.grantForm.added_days) || 0,
        deducted_days: Number(this.grantForm.deducted_days) || 0,
        attendance_rate: this.grantForm.attendance_rate === '' ? '' : Number(this.grantForm.attendance_rate),
        no_expire: this.grantForm.no_expire ? 1 : 0,
        note: this.grantForm.note || '',
      };
      // 비워 두면 base_days 를 보내지 않는다 → 서버가 법정값으로 다시 계산한다.
      if (this.grantForm.base_days !== '' && this.grantForm.base_days !== null) {
        body.base_days = Number(this.grantForm.base_days);
      }
      this.$http.post('/api/leave/grant', body)
        .then(() => {
          this.saving = false;
          this.loadStats(); this.loadTargets();
          // 모달은 닫지 않는다 — 다른 연차를 이어서 고칠 수 있게.
          this.$http.post('/api/leave/grant/list', { member_id: this.grantTarget.member_id, as_of: ymd(this.asOf) })
            .then(res => { this.grantYears = res.data.rows || []; })
            .catch(() => {});
          this.$vs.notify({ title: '저장', text: this.selectedYear + '년차 부여를 반영했습니다.', color: 'success' });
        })
        .catch(e => { this.saving = false; this.httpError(e); });
    },
    openRecord(r) {
      this.recordTarget = r;
      this.recordForm = {
        stage: r.next_stage || 'notify',
        acted_on: new Date(),
        document: '',
        note: '',
      };
      this.recordModal = true;
    },
    saveRecord() {
      this.saving = true;
      this.$http.post('/api/leave/promotion/record', {
        member_id: this.recordTarget.member_id,
        as_of: ymd(this.asOf),
        stage: this.recordForm.stage,
        acted_on: ymd(this.recordForm.acted_on),
        remain_days: this.recordTarget.remain_days,
        document: this.recordForm.document || '',
        note: this.recordForm.note || '',
      })
        .then(() => {
          this.saving = false; this.recordModal = false;
          this.loadTargets();
          this.$vs.notify({ title: '기록', text: '사용촉진 조치를 기록했습니다.', color: 'success' });
        })
        .catch(e => { this.saving = false; this.httpError(e); });
    },
    httpError(error) {
      const t = error.toString();
      if (t.includes('401')) {
        alert('권한이 없거나 로그인이 필요합니다.');
        this.$router.push({ path: '/pages/login?to=' + this.$router.history.current.path });
        return;
      }
      this.$vs.notify({ title: 'Error', text: t, color: 'danger' });
    },
  },
  created() {
    this.loadStats();
    this.loadTargets();
  },
};
</script>

<style scoped>
.ls-date { display: inline-block; width: 150px; }
.ls-count { font-weight: 600; }
.ls-scroll { overflow-x: auto; }
.ls-table { width: 100%; border-collapse: collapse; font-size: 13px; white-space: nowrap; }
.ls-table th, .ls-table td { padding: 7px 9px; border-bottom: 1px solid #ececf0; text-align: center; }
.ls-table th { background: #f7f7fa; font-weight: 500; color: #4b4b57; }
.ls-name { font-weight: 400; text-align: left; }
.ls-strong { font-weight: 600; }
.ls-added { color: #2d8a34; }
.ls-deducted { color: #c0392b; }
.ls-pending { color: #f0a020; }
.ls-rate { color: #c0392b; font-weight: 600; }
.ls-muted { color: #9a9aa4; }
.ls-warn { color: #c0392b; font-weight: 600; }
.ls-stage { display: inline-block; margin-right: 6px; }
.ls-expire-soon { background: #fff9f0; }
.ls-overdue { background: #fdf1f0; }
.ls-label { display: block; font-size: 12px; color: #6b6b76; margin-bottom: 3px; }
.ls-hint { display: block; font-size: 11px; color: #8a8a94; margin-top: 3px; }
.ls-table-sm { font-size: 12px; }
.ls-table-sm td { cursor: pointer; }
.ls-row-sel { background: #f4f3ff; }
.ls-sel-note { font-size: 13px; color: #4b4b57; }
</style>
